/**
 * What was collected, and whose each part of it is.
 *
 * ## The screen changed subject, not only its arithmetic
 *
 * It used to say *rent collected, less your commission, less what you spent — that is what the
 * landlord gets*. That was the wrong subject. **The rent is the landlord's, whole and untouched**;
 * the agency's income is the fee the tenant paid on top of it, on its own line of the same invoice.
 * So a run is no longer a deduction. It is a **distribution**: here is what came in, and here is
 * whose each part of it is. ADR-0131.
 *
 * Three figures come out of a period, and the screen shows all three at once because the operator
 * asked for exactly that — *"a staff seeing what should be paid to Landlords, our commission, and
 * marking it as paid"*. A screen showing only the transfers shows one side of a distribution and
 * invites the question "so where did the rest of it go":
 *
 * - **To transfer** — beneficiaries who accumulate a balance. These become payouts.
 * - **Kept by this agency** — its own fees. No payout row is written, because a payout against the
 *   agency is the agency owing itself money and somebody would eventually try to transfer it.
 * - **Recorded** — collected for somebody the agency does not hold a balance for, a lawyer's
 *   agreement fee being the ordinary case. The money moved and the record says where; nothing is
 *   owed and nothing here discharges it.
 *
 * Which of the three a beneficiary falls into is **`accrues` on the beneficiary record**, set when
 * it was added and never inferred from its name or its kind. An agency that does hold a lawyer's
 * fees and settles them monthly is describing an ordinary arrangement.
 *
 * ## A run is a statement, not a query
 *
 * It is drawn once over a period, it names the payments and the costs it covered, and it is read
 * back from what was written rather than recomputed. A figure that moves after a landlord has been
 * told it is a dispute; a figure that stays put is a conversation that ends.
 *
 * ## Marking paid is an act
 *
 * The transfer is a bank payment made outside this system, and claiming it happened because a
 * button was pressed here would be a statement the agency cannot back up. So the screen records
 * that it happened, when, and **how** — from the agency's own list of payout methods, which is
 * added to from this very dialog, because the moment somebody discovers the list is missing the way
 * they actually paid is the moment they are marking it paid.
 */

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFormat, useToday } from "@/lib/format";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { IconOneEnvelope, IconPayout, IconStatement } from "@/components/icons";
import { OptionPicker } from "@/components/option-picker";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Table,
} from "@/components/ui";

interface Run {
  id: string;
  reference: string;
  periodStart: string;
  periodEnd: string;
  status: "draft" | "paid" | "reversed";
  payoutCount: number;
  totalTransferred: number;
  heldAsCredit: number;
  /** What the agency kept for itself out of this period. Written on the run, not on a payout row. */
  agencyShare: number;
  /** Collected for somebody no balance is held for. Recorded, never owed. */
  recordedShare: number;
  note: string | null;
  reversalReason: string | null;
}

interface Payout {
  id: string;
  reference: string;
  beneficiaryId: string | null;
  landlordPartyId: string | null;
  landlordName?: string;
  beneficiaryName?: string;
  beneficiaryKind?: "agency" | "client" | "contact";
  methodName?: string | null;
  periodStart: string;
  periodEnd: string;
  grossCollected: number;
  expenses: number;
  netPayable: number;
  carriedIn: number;
  carriedOut: number;
  lineCount: number;
  status: "draft" | "sent" | "paid" | "reversed";
  paidOn: string | null;
  reference2?: string | null;
}

interface Line {
  id: string;
  kind: "collection" | "expense";
  sourceKind: "allocation" | "expense";
  paymentId: string | null;
  invoiceId: string | null;
  reference: string | null;
  occurredOn: string;
  description: string;
  gross: number;
  charge: number;
  net: number;
}

interface Statement {
  beneficiaryId: string | null;
  beneficiaryName: string;
  beneficiaryKind: "agency" | "client" | "contact";
  /** Whether a payout is written for this beneficiary at all. The whole shape of the run. */
  accrues: boolean;
  landlordPartyId: string | null;
  landlordName: string;
  collected: number;
  expenses: number;
  carriedIn: number;
  net: number;
  transferred: number;
  carriedOut: number;
  lineCount: number;
  problems: Array<{ what: string }>;
}

interface Distribution {
  toBeneficiaries: number;
  agency: number;
  recorded: number;
}

const RUN_TONE: Record<Run["status"], "warn" | "success" | "danger"> = {
  draft: "warn",
  paid: "success",
  reversed: "danger",
};

const PAYOUT_TONE: Record<Payout["status"], "warn" | "info" | "success" | "danger"> = {
  draft: "warn",
  sent: "info",
  paid: "success",
  reversed: "danger",
};

/** What a statement's own row says about where its money is going. */
function destinationOf(statement: Statement): { label: string; tone: "success" | "brand" | "muted" } {
  if (statement.accrues) return { label: "Transferred to them", tone: "success" };
  if (statement.beneficiaryKind === "agency") return { label: "Kept by this agency", tone: "brand" };
  return { label: "Recorded only", tone: "muted" };
}

export function PayoutsPage() {
  const fmt = useFormat();
  const today = useToday();
  const { can } = useSession();
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [openPayoutId, setOpenPayoutId] = useState<string | null>(null);
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);

  const list = useQuery({
    queryKey: ["payouts"],
    queryFn: () => api.get<{ runs: Run[]; outstanding: { count: number; total: number } }>("/payouts"),
  });

  const preview = useQuery({
    queryKey: ["payout-preview", period?.from, period?.to],
    queryFn: () =>
      api.get<{ statements: Statement[]; distribution: Distribution; heldAsCredit: number; unattributed: number }>(
        `/payouts/preview?from=${period!.from}&to=${period!.to}`,
      ),
    enabled: Boolean(period),
  });

  const runDetail = useQuery({
    queryKey: ["payout-run", openRunId],
    queryFn: () => api.get<{ run: Run; payouts: Payout[] }>(`/payouts/runs/${openRunId}`),
    enabled: Boolean(openRunId),
  });

  const statement = useQuery({
    queryKey: ["payout", openPayoutId],
    queryFn: () =>
      api.get<{
        payout: Payout;
        landlord: { name: string } | null;
        beneficiary: { name: string; kind: string; accrues: boolean } | null;
        method: { id: string; name: string } | null;
        lines: Line[];
        problems: Array<{ what: string }>;
      }>(`/payouts/${openPayoutId}`),
    enabled: Boolean(openPayoutId),
  });

  const draw = useApiMutation({
    mutationFn: (input: Record<string, string>) => api.post<{ id: string }>("/payouts/run", input),
    invalidates: [["payouts"], ["payout-preview"]],
    onSuccess: (result) => {
      setOpenRunId(result.id);
      setOpenPayoutId(null);
    },
  });

  const reverse = useApiMutation({
    mutationFn: (input: { id: string; reason: string }) => api.post(`/payouts/runs/${input.id}/reverse`, { reason: input.reason }),
    invalidates: [["payouts"], ["payout-run"], ["payout"], ["payout-preview"]],
  });

  const check = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    if (values.periodStart && values.periodEnd) setPeriod({ from: values.periodStart, to: values.periodEnd });
  };

  if (list.error) return <ErrorState error={list.error} retry={() => list.refetch()} />;

  const outstanding = list.data?.outstanding ?? { count: 0, total: 0 };
  const statements = preview.data?.statements ?? [];
  const distribution = preview.data?.distribution ?? { toBeneficiaries: 0, agency: 0, recorded: 0 };
  const anyProblem = statements.some((item) => item.problems.length > 0);

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Payouts"
        subtitle="What was collected, and whose each part of it is."
        description={
          "Draw a period to see the whole distribution: what is owed onward, what this agency kept, and what was " +
          "collected for somebody no balance is held for. A period can only be drawn once — anything already covered " +
          "by an earlier run is left out of the next one."
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardTitle
            hint={
              "Choose the dates the money came in between. Everything collected in that window that no earlier run has " +
              "covered is included, split between the beneficiaries named on the invoices it settled."
            }
          >
            <span className="inline-flex items-center gap-2">
              <IconPayout size={18} className="text-ink-400" />
              Draw a period
            </span>
          </CardTitle>

          <form onSubmit={check} className="mt-4 grid gap-3 sm:grid-cols-3">
            <Field label="From" htmlFor="periodStart">
              <Input id="periodStart" name="periodStart" type="date" required />
            </Field>
            <Field label="To" htmlFor="periodEnd">
              <Input id="periodEnd" name="periodEnd" type="date" required />
            </Field>
            <div className="flex items-end">
              <Button type="submit" variant="secondary" disabled={preview.isFetching}>
                {preview.isFetching ? "Checking…" : "Check the period"}
              </Button>
            </div>
          </form>

          {preview.error ? (
            <div className="mt-4">
              <ErrorState error={preview.error} retry={() => preview.refetch()} />
            </div>
          ) : null}

          {period && preview.data ? (
            statements.length === 0 ? (
              <div className="mt-4">
                <EmptyState
                  title="Nothing in that period is waiting to be paid out."
                  hint="Either nothing was collected between those dates, or an earlier run has already covered it."
                />
              </div>
            ) : (
              <div className="mt-5" data-payout-preview>
                {anyProblem ? (
                  <div className="mb-4">
                    {/* Written before it is saved, and again when it is read back. It has never
                        fired; if it ever does, the run must not be drawn. */}
                    <Alert tone="danger" title="These figures do not reconcile.">
                      Something changed underneath this period while it was being prepared. Check the period again before
                      drawing a run.
                    </Alert>
                  </div>
                ) : null}

                <Table
                  head={
                    <tr>
                      <th>Beneficiary</th>
                      <th className="num">Collected</th>
                      <th className="num">Costs recharged</th>
                      <th className="num">Carried in</th>
                      <th className="num">Amount</th>
                      <th>Where it goes</th>
                    </tr>
                  }
                >
                  {statements.map((item) => {
                    const destination = destinationOf(item);
                    return (
                      <tr key={item.beneficiaryId ?? "unassigned"} data-statement-for={item.beneficiaryName}>
                        <td>
                          {/* A beneficiary is a client only sometimes. A lawyer is on no client
                              list by design, so the name links to a record where there is one and
                              stands on its own where there is not. */}
                          {item.landlordPartyId ? (
                            <Link to={`/app/clients/${item.landlordPartyId}`} className="link">
                              {item.landlordName || item.beneficiaryName}
                            </Link>
                          ) : (
                            <span className="font-medium text-ink-800">{item.beneficiaryName || "Not attributed"}</span>
                          )}
                          <span className="block text-xs text-ink-400">
                            {item.lineCount} line{item.lineCount === 1 ? "" : "s"}
                          </span>
                        </td>
                        <td className="num tabular">{fmt.money(item.collected)}</td>
                        <td className="num tabular text-ink-500">{item.expenses ? fmt.money(item.expenses) : "—"}</td>
                        <td className="num tabular text-ink-500">{item.carriedIn ? fmt.money(item.carriedIn) : "—"}</td>
                        <td className="num tabular font-medium">
                          {fmt.money(item.accrues ? item.transferred : item.collected)}
                          {item.carriedOut < 0 ? (
                            <span className="block text-xs font-normal text-warn-600">
                              {fmt.money(-item.carriedOut)} owed to you, carried forward
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <Badge tone={destination.tone}>{destination.label}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </Table>

                {/*
                  A worked example rather than a definition, with this period's own figures in it.

                  The three parts are named in the order somebody reads them off the table above, and
                  they add up to what was collected — which is what makes the sentence checkable
                  rather than merely reassuring.
                */}
                <p className="mt-3 text-sm text-ink-500" data-payout-distribution>
                  {fmt.money(statements.reduce((sum, item) => sum + item.collected, 0))} was collected in this period.{" "}
                  <span className="font-medium text-ink-800">{fmt.money(distribution.toBeneficiaries)}</span> is to be
                  transferred onward, {fmt.money(distribution.agency)} is kept by this agency, and{" "}
                  {fmt.money(distribution.recorded)} was collected for somebody no balance is held for.
                </p>

                {preview.data.heldAsCredit > 0 ? (
                  <p className="mt-2 text-sm text-ink-500">
                    A further {fmt.money(preview.data.heldAsCredit)} was received in this period but is not applied to any
                    invoice yet, so it belongs to nobody and is not included. It will be included once it is allocated.
                  </p>
                ) : null}

                {preview.data.unattributed > 0 ? (
                  <p className="mt-2 text-sm text-ink-500">
                    {preview.data.unattributed} entr{preview.data.unattributed === 1 ? "y names" : "ies name"} no
                    beneficiary — a charge nobody has attributed, or an invoice raised against no unit. Nothing is
                    guessed at. Name a beneficiary on the charge to bring it into the next run.
                  </p>
                ) : null}

                {can("payout.record") ? (
                  <form
                    className="mt-5 flex flex-wrap items-end gap-3 border-t border-ink-100 pt-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const values = formValues(event.currentTarget);
                      draw.mutate({ periodStart: period.from, periodEnd: period.to, note: values.note ?? "" });
                    }}
                  >
                    {draw.errorMessage ? (
                      <div className="w-full">
                        <Alert tone="danger">{draw.errorMessage}</Alert>
                      </div>
                    ) : null}
                    <Field label="Note (optional)" htmlFor="note">
                      <Input id="note" name="note" placeholder="August remittances" className="w-56" />
                    </Field>
                    <Button type="submit" disabled={draw.isPending || anyProblem}>
                      {draw.isPending ? "Drawing…" : "Draw this run"}
                    </Button>
                  </form>
                ) : null}
              </div>
            )
          ) : null}
        </Card>

        <Card>
          <CardTitle hint="Statements drawn and not yet marked as transferred.">
            <span className="inline-flex items-center gap-2">
              <IconOneEnvelope size={18} className="text-ink-400" />
              Awaiting transfer
            </span>
          </CardTitle>
          <p className="tabular text-3xl font-semibold text-ink-900">{fmt.money(outstanding.total)}</p>
          <p className="mt-1 text-sm text-ink-500">
            {outstanding.count === 0
              ? "Every statement you have drawn has been marked as transferred."
              : `${outstanding.count} statement${outstanding.count === 1 ? "" : "s"} across your beneficiaries.`}
          </p>
        </Card>
      </div>

      <Card className="mt-5" padded={false}>
        <div className="p-4 sm:p-5">
          <CardTitle hint="Each one names the payments and the costs it covered.">Runs</CardTitle>
        </div>

        {list.isLoading ? (
          <div className="p-5">
            <Spinner />
          </div>
        ) : !list.data?.runs.length ? (
          <div className="p-5">
            <EmptyState title="No runs yet." hint="Check a period above, then draw it." />
          </div>
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>Period</th>
                <th className="num">Statements</th>
                <th className="num">Transferred</th>
                <th className="num">Kept</th>
                <th>Status</th>
                <th />
              </tr>
            }
          >
            {list.data.runs.map((run) => (
              <tr key={run.id}>
                <td className="mono text-xs font-medium">{run.reference}</td>
                <td className="text-ink-600">
                  {fmt.date(run.periodStart)} – {fmt.date(run.periodEnd)}
                </td>
                <td className="num tabular">{run.payoutCount}</td>
                <td className="num tabular font-medium">{fmt.money(run.totalTransferred)}</td>
                {/* The agency's own share, on the record rather than only in the preview: a run
                    read back a month later should say what the whole distribution was. */}
                <td className="num tabular text-ink-500">{fmt.money(run.agencyShare)}</td>
                <td>
                  <Badge tone={RUN_TONE[run.status]} dot>
                    {run.status}
                  </Badge>
                </td>
                <td className="num">
                  <button
                    type="button"
                    className="text-sm text-ink-500 hover:text-ink-800"
                    onClick={() => {
                      setOpenRunId(openRunId === run.id ? null : run.id);
                      setOpenPayoutId(null);
                    }}
                  >
                    {openRunId === run.id ? "Hide" : "Open"}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {openRunId && runDetail.data ? (
        <Card className="mt-5" padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle
              hint={
                runDetail.data.run.status === "reversed"
                  ? `Reversed. ${runDetail.data.run.reversalReason ?? ""}`
                  : `${runDetail.data.run.payoutCount} statement${runDetail.data.run.payoutCount === 1 ? "" : "s"} over ${fmt.date(runDetail.data.run.periodStart)} to ${fmt.date(runDetail.data.run.periodEnd)}.`
              }
              action={
                runDetail.data.run.status !== "reversed" && can("payout.record") ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={reverse.isPending}
                    onClick={() => {
                      const reason = window.prompt(
                        "Reversing releases every payment and cost this run covered, so the next run picks them up again. Why is it being reversed?",
                      );
                      if (reason && reason.trim().length >= 3) reverse.mutate({ id: openRunId, reason: reason.trim() });
                    }}
                  >
                    Reverse this run
                  </Button>
                ) : undefined
              }
            >
              {runDetail.data.run.reference}
            </CardTitle>
            {reverse.errorMessage ? <Alert tone="danger">{reverse.errorMessage}</Alert> : null}

            {/*
              The rest of the distribution, stated on the run itself.

              Only the beneficiaries that accumulate got a row in the table below — there is nothing
              to pay the agency and nothing to pay somebody no balance is held for — so without this
              line the run would appear to have lost the difference between what was collected and
              what it is transferring.
            */}
            <p className="mt-2 text-sm text-ink-500">
              Besides the statements below, {fmt.money(runDetail.data.run.agencyShare)} was kept by this agency and{" "}
              {fmt.money(runDetail.data.run.recordedShare)} was collected for somebody no balance is held for.
            </p>
          </div>

          <Table
            head={
              <tr>
                <th>Beneficiary</th>
                <th>Statement</th>
                <th className="num">Collected</th>
                <th className="num">Costs recharged</th>
                <th className="num">Payable</th>
                <th>Status</th>
                <th />
              </tr>
            }
          >
            {runDetail.data.payouts.map((payout) => (
              <tr key={payout.id} data-payout-for={payout.beneficiaryName}>
                <td>
                  {payout.landlordPartyId ? (
                    <Link to={`/app/clients/${payout.landlordPartyId}`} className="link">
                      {payout.beneficiaryName || payout.landlordName}
                    </Link>
                  ) : (
                    <span className="font-medium text-ink-800">{payout.beneficiaryName || payout.landlordName}</span>
                  )}
                </td>
                <td className="mono text-xs">{payout.reference}</td>
                <td className="num tabular">{fmt.money(payout.grossCollected)}</td>
                <td className="num tabular text-ink-500">{fmt.money(payout.expenses)}</td>
                <td className="num tabular font-medium">{fmt.money(payout.netPayable)}</td>
                <td>
                  <Badge tone={PAYOUT_TONE[payout.status]} dot>
                    {payout.status}
                  </Badge>
                  {payout.methodName ? <span className="block text-xs text-ink-400">{payout.methodName}</span> : null}
                </td>
                <td className="num">
                  <button
                    type="button"
                    className="text-sm text-ink-500 hover:text-ink-800"
                    onClick={() => setOpenPayoutId(openPayoutId === payout.id ? null : payout.id)}
                  >
                    {openPayoutId === payout.id ? "Hide" : "Statement"}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}

      {openPayoutId && statement.data ? (
        <Card className="mt-5" padded={false}>
          <div className="p-4 sm:p-5">
            <CardTitle
              hint={`${statement.data.payout.lineCount} line${statement.data.payout.lineCount === 1 ? "" : "s"}, covering ${fmt.date(statement.data.payout.periodStart)} to ${fmt.date(statement.data.payout.periodEnd)}.`}
            >
              <span className="inline-flex items-center gap-2">
                <IconStatement size={18} className="text-ink-400" />
                {statement.data.beneficiary?.name ?? statement.data.landlord?.name ?? "Beneficiary"} ·{" "}
                {statement.data.payout.reference}
              </span>
            </CardTitle>

            {statement.data.problems.length ? (
              <Alert tone="danger" title="This statement no longer adds up.">
                {statement.data.problems.map((problem) => problem.what).join("; ")}
              </Alert>
            ) : null}

            <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
              {[
                ["Collected", statement.data.payout.grossCollected],
                ["Costs recharged", statement.data.payout.expenses],
                ["Carried in", statement.data.payout.carriedIn],
                ["Payable", statement.data.payout.netPayable],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt className="eyebrow">{label}</dt>
                  <dd className="tabular font-medium">{fmt.money(Number(value))}</dd>
                </div>
              ))}
            </dl>

            {statement.data.payout.netPayable < 0 ? (
              <p className="mt-3 text-sm text-warn-700">
                Costs met on this beneficiary's behalf came to more than was collected for them in this period. Nothing is
                transferred; {fmt.money(-statement.data.payout.netPayable)} is carried forward and deducted from the next
                run.
              </p>
            ) : null}

            <div className="mt-4 border-t border-ink-100 pt-4">
              {statement.data.payout.status === "paid" ? (
                <p className="text-sm text-ink-600" data-payout-paid>
                  Transferred {statement.data.payout.paidOn ? fmt.date(statement.data.payout.paidOn) : ""}
                  {statement.data.method ? ` by ${statement.data.method.name}` : ""}
                  {statement.data.payout.reference2 ? ` · ${statement.data.payout.reference2}` : ""}.
                </p>
              ) : statement.data.payout.netPayable > 0 && can("payout.record") ? (
                <MarkPaid payoutId={statement.data.payout.id} today={today} />
              ) : null}
            </div>
          </div>

          <Table
            head={
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Detail</th>
                <th className="num">Collected</th>
                <th className="num">Recharged</th>
                <th className="num">Effect</th>
              </tr>
            }
          >
            {statement.data.lines.map((line) => (
              <tr key={line.id}>
                <td className="text-ink-600">{fmt.date(line.occurredOn)}</td>
                <td className="mono text-xs">
                  {line.paymentId ? (
                    <Link to={`/app/payments/${line.paymentId}`} className="link">
                      {line.reference ?? "—"}
                    </Link>
                  ) : (
                    (line.reference ?? "—")
                  )}
                </td>
                <td>{line.description}</td>
                <td className="num tabular">{line.gross ? fmt.money(line.gross) : "—"}</td>
                <td className="num tabular text-ink-500">{line.charge ? fmt.money(line.charge) : "—"}</td>
                <td className="num tabular font-medium">{fmt.money(line.net)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Recording that the money actually went out.
 *
 * Three fields and only one of them is required by anything: **how it was paid is optional**, and
 * that is deliberate — refusing a payout because the method was left blank would mean somebody
 * could not write down that the money went out, which is the one fact this screen exists to
 * capture. The picker adds to the agency's own list where the choice is made, because the moment
 * somebody finds the list is missing the way they actually paid is this moment.
 *
 * The date defaults to today and is editable, because the software learns about most transfers
 * after they have happened.
 */
function MarkPaid({ payoutId, today }: { payoutId: string; today: string }) {
  const [methodId, setMethodId] = useState<string | null>(null);

  const markPaid = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/payouts/${payoutId}/paid`, input),
    invalidates: [["payouts"], ["payout-run"], ["payout"]],
  });

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        const values = formValues(event.currentTarget);
        markPaid.mutate({
          paidOn: values.paidOn || null,
          methodId: methodId || null,
          paymentReference: values.paymentReference || null,
        });
      }}
    >
      {markPaid.errorMessage ? (
        <div className="w-full">
          <Alert tone="danger">{markPaid.errorMessage}</Alert>
        </div>
      ) : null}

      <Field label="Paid on" htmlFor="paidOn">
        <Input id="paidOn" name="paidOn" type="date" defaultValue={today} className="w-44" />
      </Field>

      <div className="w-52">
        <Field label="How it was paid" htmlFor="payoutMethod">
          <OptionPicker
            kind="payout_method"
            id="payoutMethod"
            value={methodId}
            onChange={setMethodId}
            emptyLabel="Not recorded"
            ariaLabel="How it was paid"
          />
        </Field>
      </div>

      <Field label="Reference" htmlFor="paymentReference">
        <Input id="paymentReference" name="paymentReference" placeholder="Bank reference" className="w-44" />
      </Field>

      <div className="pb-1">
        <Button type="submit" disabled={markPaid.isPending}>
          {markPaid.isPending ? "Recording…" : "Mark transferred"}
        </Button>
      </div>
    </form>
  );
}
